import React, { useState, useEffect, useMemo } from 'react';
import Card from '../../components/Card/Card';
import Badge from '../../components/Badge/Badge';
import Button from '../../components/Button/Button';
import Input from '../../components/Input/Input';
import Select from '../../components/Select/Select';
import DataTable from '../../components/DataTable/DataTable';
import Modal from '../../components/Modal/Modal';
import { FiSearch, FiCheckCircle, FiUser, FiBookOpen, FiBook, FiUsers, FiRefreshCw, FiAlertCircle, FiDollarSign } from 'react-icons/fi';
import api from '../../api';
import './IssueReturn.css';

const IssueReturn = ({ showAlert, showConfirm }) => {
  const [activeTab, setActiveTab] = useState('issue');
  const [loading, setLoading] = useState(false);

  // Data State
  const [stats, setStats] = useState(null);
  const [activeIssues, setActiveIssues] = useState([]);
  const [members, setMembers] = useState([]);
  const [books, setBooks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [rules, setRules] = useState({ BorrowPeriod: 14 });

  // Search/Filter State
  const [memberSearch, setMemberSearch] = useState('');
  const [bookSearch, setBookSearch] = useState('');
  const [selCategory, setSelCategory] = useState('');
  const [selSubCat, setSelSubCat] = useState('');
  const [tableSearch, setTableSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'IssueId', direction: 'desc' });

  // Selection State
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedBook, setSelectedBook] = useState(null);

  // Form State
  const [issueForm, setIssueForm] = useState({ issueDate: new Date().toISOString().split('T')[0], dueDate: '' });

  // Return Processing State
  const [returnModal, setReturnModal] = useState({ isOpen: false, issueId: null, fine: 0, paid: 0, remarks: '', memberName: '', bookTitle: '' });

  /* ── Initial Fetch ── */
  const refreshData = async () => {
    setLoading(true);
    try {
      const resp = await Promise.all([
        api.get('/issue.php'), api.get('/members.php'), api.get('/books.php'),
        api.get('/categories.php'), api.get('/subcategories.php'), api.get('/rules.php'), api.get('/stats.php')
      ]);
      const [issues, mems, bks, cats, subs, rls, sts] = resp.map(r => r.data);

      setActiveIssues(Array.isArray(issues) ? issues : []);
      setMembers(Array.isArray(mems) ? mems : []);
      setBooks(Array.isArray(bks) ? bks : []);
      setCategories(Array.isArray(cats) ? cats : []);
      setSubCategories(Array.isArray(subs) ? subs : []);
      setRules(rls || { BorrowPeriod: 14 });
      setStats(sts);

      const period = rls?.BorrowPeriod || 14;
      const defaultDue = new Date(Date.now() + period * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      setIssueForm(p => ({ ...p, dueDate: defaultDue }));
    } catch (err) { console.error("Fetch Error:", err); } finally { setLoading(false); }
  };

  useEffect(() => { refreshData(); }, []);

  const handleIssueDateChange = (newDate) => {
    const period = parseInt(rules.BorrowPeriod) || 14;
    const d = new Date(newDate); d.setDate(d.getDate() + period);
    setIssueForm({ issueDate: newDate, dueDate: d.toISOString().split('T')[0] });
  };

  /* ── Formatters ── */
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    const day = d.getDate().toString().padStart(2, '0');
    const month = d.toLocaleString('en-GB', { month: 'short' });
    const year = d.getFullYear();
    return `${day} ${month}, ${year}`;
  };

  /* ── Search Results ── */
  const memberResults = useMemo(() => {
    if (!memberSearch.trim()) return [];
    const q = memberSearch.toLowerCase();
    return (members || []).filter(m =>
      m.Status === 'Active' && (
        m.Name?.toLowerCase().includes(q) || m.MemberId?.toString().includes(q) || m.Phone?.includes(q)
      )
    ).slice(0, 5);
  }, [members, memberSearch]);

  const bookResults = useMemo(() => {
    const dataList = Array.isArray(books) ? [...books] : [];
    let data = dataList;
    if (selCategory) data = data.filter(b => b.SubjectId == selCategory);
    if (selSubCat) data = data.filter(b => b.SubId == selSubCat);
    if (!bookSearch.trim() && !selCategory) return [];
    if (bookSearch.trim()) {
      const q = bookSearch.toLowerCase();
      data = data.filter(b => b.Title?.toLowerCase().includes(q) || b.AccessionNo?.toLowerCase().includes(q));
    }
    return data.slice(0, 10);
  }, [books, bookSearch, selCategory, selSubCat]);

  const filteredTransactions = useMemo(() => {
    const dataList = Array.isArray(activeIssues) ? activeIssues : [];
    let data = [];

    // Split logically per tab to avoid reference mutation bugs
    if (activeTab === 'transactions') {
      data = dataList.filter(i => i.Status !== 'Returned');
    } else if (activeTab === 'returned') {
      data = dataList.filter(i => i.Status === 'Returned');
    } else {
      // Full History - Always work on a brand new copy to prevent UI duplication bug
      data = [...dataList];
    }

    if (tableSearch.trim()) {
      const q = tableSearch.toLowerCase();
      data = data.filter(i => i.MemberName?.toLowerCase().includes(q) || i.BookTitle?.toLowerCase().includes(q) || i.AccessionNo?.toLowerCase().includes(q));
    }

    if (sortConfig.key) {
      data.sort((a, b) => {
        const aVal = a[sortConfig.key] || '';
        const bVal = b[sortConfig.key] || '';
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return data;
  }, [activeIssues, activeTab, tableSearch, sortConfig]);

  const handleSort = (key) => setSortConfig(p => ({ key, direction: p.key === key && p.direction === 'asc' ? 'desc' : 'asc' }));

  /* ── Handlers ── */
  const handleIssue = async () => {
    if (!selectedMember || !selectedBook) return showAlert("Validation", "Select member and book.", "confirm");
    setLoading(true);
    try {
      const res = await api.post('/issue.php', { MemberId: selectedMember.MemberId, AccessionNo: selectedBook.AccessionNo, IssueDate: issueForm.issueDate, DueDate: issueForm.dueDate });
      const result = res.data;
      showAlert("Success", "Book issued successfully!", "success"); refreshData(); setSelectedMember(null); setSelectedBook(null);
    } catch (err) { showAlert("Issue Failed", err.message, "confirm"); } finally { setLoading(false); }
  };

  const openReturnModal = (row) => {
    setReturnModal({
      isOpen: true,
      issueId: row.IssueId,
      fine: row.Fine || 0,
      paid: row.Fine || 0,
      remarks: row.Fine > 0 ? 'Late fine collected' : 'Returned on time',
      memberName: row.MemberName,
      bookTitle: row.BookTitle
    });
  };

  const processReturn = async () => {
    setLoading(true);
    try {
      const res = await api.put('/issue.php', {
          IssueId: returnModal.issueId,
          FinePaid: returnModal.paid,
          Remarks: returnModal.remarks
      });
      const result = res.data;

      setReturnModal(p => ({ ...p, isOpen: false }));
      showAlert("Returned", `Book returned successfully! \nFine Calculated: ₹${result.fine}\nAmount Collected: ₹${returnModal.paid}`, "success");
      refreshData();
    } catch (err) {
      showAlert("Error", "Failed to process return.", "confirm");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="issue-return-page">
      <div className="page-header"><div><h2 className="page-header__title">Issue & Return</h2><p className="page-header__sub">Circulation management and tracking</p></div></div>
      <div className="members-kpi" style={{ marginBottom: 30 }}>
        <Card padding="sm" className="kpi-card kpi-card--primary"><div className="kpi-card__inner"><div><p className="kpi-card__label">Total Inventory</p><p className="kpi-card__value">{stats?.totalBooks || 0}</p></div><div className="kpi-card__icon"><FiBook /></div></div></Card>
        <Card padding="sm" className="kpi-card kpi-card--warning"><div className="kpi-card__inner"><div><p className="kpi-card__label">Currently Issued</p><p className="kpi-card__value">{stats?.totalIssued || 0}</p></div><div className="kpi-card__icon"><FiRefreshCw /></div></div></Card>
        <Card padding="sm" className="kpi-card kpi-card--danger"><div className="kpi-card__inner"><div><p className="kpi-card__label">Overdue</p><p className="kpi-card__value">{stats?.totalOverdue || 0}</p></div><div className="kpi-card__icon"><FiAlertCircle /></div></div></Card>
      </div>
      <div className="ir-tabs">
        {[
          { key: 'issue', label: 'Issue Book' },
          { key: 'transactions', label: 'Active Issues' },
          { key: 'returned', label: 'Returned Items' },
          { key: 'history', label: 'Full History' }
        ].map(t => (
          <button key={t.key} className={`ir-tab ${activeTab === t.key ? 'ir-tab--active' : ''}`} onClick={() => setActiveTab(t.key)}>{t.label}</button>
        ))}
      </div>
      <div className="ir-body" style={{ marginBottom: 30 }}>
        {activeTab === 'issue' ? (
          <div className="issue-grid">
            <div className="issue-section issue-section--main">
              <Card padding="md" className={`issue-step-card ${selectedMember ? 'issue-step-card--done' : ''}`}><div className="step-badge">1</div><Card.Header><Card.Title>Find Member</Card.Title></Card.Header>{selectedMember ? (<div className="selected-preview selected-preview--member"><div className="sel-avatar"><FiUser /></div><div className="sel-info"><strong>{selectedMember.Name}</strong><p>ID: {selectedMember.MemberId} | {selectedMember.MemberType}</p></div><button className="sel-remove" onClick={() => setSelectedMember(null)}>Change</button></div>) : (<div className="ir-search-box"><Input icon={<FiSearch />} placeholder="Search active member…" value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} />{memberResults.length > 0 && (<div className="ir-search-results">{memberResults.map(m => (<div key={m.MemberId} className="search-res-item" onClick={() => { setSelectedMember(m); setMemberSearch(''); }}><div className="res-book-title">{m.Name} <small>({m.MemberId})</small></div></div>))}</div>)}</div>)}</Card>
              <Card padding="md" className={`issue-step-card ${selectedBook ? 'issue-step-card--done' : ''}`} style={{ marginTop: 20, paddingBottom: 300 }}><div className="step-badge">2</div><Card.Header><Card.Title>Find Book</Card.Title></Card.Header>{selectedBook ? (<div className="selected-preview selected-preview--book"><div className="sel-avatar sel-avatar--book"><FiBookOpen /></div><div className="sel-info"><strong>{selectedBook.Title}</strong><p>Acc No: {selectedBook.AccessionNo} | {selectedBook.Author}</p></div><button className="sel-remove" onClick={() => setSelectedBook(null)}>Change</button></div>) : (<div className="book-finder"><div className="book-finder-filters"><Select options={[{ value: '', label: 'Category' }, ...categories.map(c => ({ value: c.SubjectId, label: c.Name }))]} value={selCategory} onChange={(e) => { setSelCategory(e.target.value); setSelSubCat(''); }} /><Select options={[{ value: '', label: 'Sub-Cat' }, ...subCategories.filter(s => !selCategory || s.SubjectId == selCategory).map(s => ({ value: s.SubId, label: s.SubName }))]} value={selSubCat} onChange={(e) => setSelSubCat(e.target.value)} /></div><div className="ir-search-box" style={{ marginTop: 12 }}><Input icon={<FiSearch />} placeholder="Search book…" value={bookSearch} onChange={(e) => setBookSearch(e.target.value)} />{bookResults.length > 0 && (<div className="ir-search-results ir-search-results--books">{bookResults.map(b => (<div key={b.AccessionNo} className="search-res-item" onClick={() => { setSelectedBook(b); setBookSearch(''); }}><div className="res-book-title">{b.Title}</div><div className="res-book-meta">{b.AccessionNo} | {b.Author}</div></div>))}</div>)}</div></div>)}</Card>
            </div>
            <div className="issue-section issue-section--sidebar"><Card padding="md"><Card.Header><Card.Title>Finalize Issue</Card.Title></Card.Header><div className="finalize-form"><Input label="Issue Date" type="date" value={issueForm.issueDate} onChange={(e) => handleIssueDateChange(e.target.value)} /><Input label="Due Date" type="date" value={issueForm.dueDate} onChange={(e) => setIssueForm(p => ({ ...p, dueDate: e.target.value }))} /></div><Button variant="primary" fullWidth loading={loading} disabled={!selectedMember || !selectedBook} onClick={handleIssue} style={{ marginTop: 20 }}>Confirm Issue</Button></Card></div>
          </div>
        ) : (
          <Card padding="none">
            <div className="shared-toolbar">
              <div className="shared-search-box">
                <FiSearch className="shared-search-box__icon" />
                <input
                  type="text"
                  placeholder="Search by member, book or Accession No…"
                  value={tableSearch}
                  onChange={(e) => setTableSearch(e.target.value)}
                  className="shared-search-box__input"
                />
              </div>
            </div>
            <DataTable
              columns={[
                { key: 'MemberName', label: 'Member', sortable: true },
                {
                  key: 'BookTitle', label: 'Book Name', sortable: true, render: (val, row) => (
                    <div>
                      <div style={{ fontWeight: 600 }}>{val}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Acc No: {row.AccessionNo}</div>
                    </div>
                  )
                },
                { key: 'IssueDate', label: 'Issued On', sortable: true, render: (val) => formatDate(val) },
                activeTab === 'returned' 
                  ? { key: 'ReturnDate', label: 'Returned On', sortable: true, render: (val) => <span style={{ color: 'var(--success)', fontWeight: 500 }}>{formatDate(val)}</span> }
                  : { key: 'DueDate', label: 'Due Date', sortable: true, render: (val) => <span style={{ color: 'var(--danger)', fontWeight: 500 }}>{formatDate(val)}</span> },
                {
                  key: 'DaysLeft', label: 'Days Left', render: (_, row) => {
                    if (row.Status === 'Returned') return '—';
                    const diff = Math.round((new Date(row.DueDate).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / (86400000));
                    return <span style={{ color: diff < 0 ? 'var(--danger)' : 'inherit', fontWeight: diff <= 1 ? 600 : 400 }}>{diff === 0 ? 'Today' : (diff === 1 ? 'Tomorrow' : (diff === -1 ? 'Yesterday' : `${diff} days`))}</span>;
                  }
                },
                { key: 'Status', label: 'Status', sortable: true, render: (val) => <Badge variant={val === 'Returned' ? 'success' : (val === 'Overdue' ? 'danger' : 'warning')} dot>{val}</Badge> },
                { key: 'Fine', label: 'Fine', sortable: true, render: (val) => val > 0 ? <strong style={{ color: 'var(--danger)' }}>₹{parseFloat(val).toLocaleString()}</strong> : '—' },
                { key: 'actions', label: 'Action', render: (_, row) => row.Status !== 'Returned' ? <Button variant="secondary" size="sm" icon={<FiRefreshCw />} onClick={() => openReturnModal(row)}>Return</Button> : '—' }
              ]}
              data={filteredTransactions}
              loading={loading}
              onSort={handleSort}
              sortConfig={sortConfig}
            />
          </Card>
        )}
      </div>

      <Modal isOpen={returnModal.isOpen} onClose={() => setReturnModal(p => ({ ...p, isOpen: false }))} title="Process Book Return" size="md">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ background: 'var(--gray-50)', padding: '12px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)' }}>Returning Book</p>
            <h4 style={{ margin: '2px 0 8px 0', fontSize: '15px' }}>{returnModal.bookTitle}</h4>
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)' }}>Issued To</p>
            <p style={{ margin: 0, fontWeight: 600 }}>{returnModal.memberName}</p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Input label="Calculated Fine" value={`₹${returnModal.fine}`} disabled prefix={<FiAlertCircle />} />
            <Input
              label="Amount Collected"
              type="number"
              value={returnModal.paid}
              onChange={(e) => setReturnModal(p => ({ ...p, paid: e.target.value }))}
              prefix={<FiDollarSign />}
              hint={returnModal.paid < returnModal.fine ? "Fine partially waived" : ""}
            />
          </div>

          <Input label="Remarks" placeholder="Reason for waiving or general remarks..." value={returnModal.remarks} onChange={(e) => setReturnModal(p => ({ ...p, remarks: e.target.value }))} />

          <div style={{ marginTop: '12px', display: 'flex', gap: '12px' }}>
            <Button variant="primary" fullWidth onClick={processReturn} loading={loading}>Finalize Return</Button>
            <Button variant="secondary" onClick={() => setReturnModal(p => ({ ...p, isOpen: false }))}>Cancel</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default IssueReturn;
